<template>
    <div class="tile-content">
        <div class="grid-content">
            <div class="grid-item grid-item-half grid-item-break-half">
                <div>
                    <v-checkbox
                        label="Schedule recurring delivery of your export"
                        :value="true"
                        :selected="enableRecurring"
                        :disabled="disableToggleRecurring"
                        :validate="checkboxValidation"
                        @input="toggleRecurring"/>
                </div>
            </div>
            <div class="grid-item grid-item-full" v-if="enableRecurring">
                <p class="stack-s">Schedule</p>
                <v-radio
                    class="stack-m"
                    v-for="option in repeatOptions"
                    group="schedule-option"
                    :label="option.label"
                    :value="option.value"
                    :key="option.value"
                    :model="scheduleOption"
                    :validate="validate.instruction"
                    @update="updateScheduleOptionMethod"
                />

                <div class="grid-item display-flex">
                    <template v-if="showDayOfMonthDropDown">
                        <v-select
                            label="Every"
                            class="inline-s"
                            :value="dayOfMonth"
                            :options="dayOptions"
                            @input="updateDayOfMonth"/>
                    </template>
                    <v-select
                        class="inline-s"
                        label="Every"
                        v-if="showWeeklyList"
                        :options="weekdays"
                        :value="selectedDay"
                        @input="updateSelectedDay"/>

                    <v-date-input
                        label="Start Date / Time"
                        type="datetime"
                        :value="startTime"
                        :validate="validate.start"
                        @input="updateDate"/>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import moment from 'moment';
    import _ from '@/common/helpers';
    import {
        vCheckbox, vDateInput, vRadio, vSelect,
    } from '@/components/form';
    import {DataExportModel, RecurringModel} from '@/common/models/data-export-model';

    export default {
        name: 'schedule-form',
        components: {
            vCheckbox,
            vRadio,
            vSelect,
            vDateInput,
        },
        props: {
            recurring: {
                validator: value => value === null || typeof value === 'object',
                required: true,
            },
            validate: {
                type: Object,
                required: true,
            },
            checkboxValidation: {
                type: Object,
                required: true,
            },
            enableRecurring: {
                type: Boolean,
                required: true,
            },
            disableToggleRecurring: {
                type: Boolean,
                required: true,
            },
        },
        data() {
            const weekdays = moment.weekdays();
            const weekdaysMin = moment.weekdaysMin();

            return {
                repeatOptions: Object.values(DataExportModel.Schedules)
                    .map(value => ({value, label: _.capitalize(value.toLowerCase())})),
                dayOptions: new Array(31)
                    .fill(0)
                    .map((v, i) => {
                        const value = i + 1;
                        const label = this.$filters.ordinal(value);
                        return {
                            value,
                            label,
                        };
                    }),
                weekdays: weekdaysMin.map((value, index) => ({
                    value: value.toUpperCase(),
                    label: weekdays[index],
                })),
            };
        },
        computed: {
            recurringInfo() {
                return !this.recurring
                    ? {}
                    : RecurringModel.parse(this.recurring.instruction, this.recurring.start);
            },
            scheduleOption() {
                return this.recurringInfo.scheduleOption;
            },
            selectedDay() {
                return this.recurringInfo.selectedDay;
            },
            dayOfMonth() {
                return +this.recurringInfo.dayOfMonth;
            },
            startTime() {
                return this.recurring.start ? this.$tz().readUTC(this.recurring.start) : null;
            },
            showWeeklyList() {
                return this.scheduleOption === DataExportModel.Schedules.weekly;
            },
            showDayOfMonthDropDown() {
                return this.scheduleOption === DataExportModel.Schedules.monthly;
            },
        },
        methods: {
            updateScheduleOptionMethod(scheduleOption) {
                const map = {
                    [DataExportModel.Schedules.daily]: {},
                    [DataExportModel.Schedules.monthly]: {
                        dayOfMonth: this.dayOptions[0].value,
                    },
                    [DataExportModel.Schedules.weekly]: {
                        selectedDay: this.weekdays[0].value,
                    },
                };
                const instruction = RecurringModel.makeInstruction(this.recurringInfo, {
                    scheduleOption,
                    ...map[scheduleOption],
                });
                this.$emit('update', {instruction});
            },
            updateDayOfMonth(dayOfMonth) {
                const instruction = RecurringModel.makeInstruction(this.recurringInfo, {
                    dayOfMonth,
                });
                this.$emit('update', {instruction});
            },
            updateSelectedDay(selectedDay) {
                const instruction = RecurringModel.makeInstruction(this.recurringInfo, {
                    selectedDay,
                });
                this.$emit('update', {instruction});
            },
            updateDate(value) {
                this.$emit('update', {start: this.$tz().getRaw(value)});
            },
            toggleRecurring(enableRecurring) {
                this.$emit('update', enableRecurring ? new RecurringModel() : null);
            },
        },
    };
</script>
